import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, UserGroupIcon, SparklesIcon, UserIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import logo from './img/logo.svg';

const PublicHeader = () => {
    const [showStickyHeader, setShowStickyHeader] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            setShowStickyHeader(scrollPosition > 200);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const htmlElement = document.documentElement;
        htmlElement.classList.add("tailwind-html");

        return () => {
            htmlElement.classList.remove("tailwind-html");
        };
    }, []);

    useEffect(() => {
        document.body.classList.add("tailwind-body");

        return () => {
            document.body.classList.remove("tailwind-body");
        };
    }, []);
    return (
        <div>
            {/* Sticky Header */}
            <div
                className={`fixed inset-x-0 top-4 z-50 flex justify-center transition-all duration-300 ${showStickyHeader
                    ? "translate-y-0 opacity-100"
                    : "-translate-y-full opacity-0"
                    }`}
            >
                <nav className="w-full max-w-[750px] mx-4 flex items-center justify-between rounded-full bg-white/80 backdrop-blur-md shadow-md shadow-sky-900/5 ring-1 ring-slate-200 px-5 py-4">
                    <Link to="/" className="flex items-center space-x-2">
                        <img src={logo} alt="Emissary Health" className="h-8 w-auto" />
                    </Link>
                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-x-4">
                        <Link
                            to="/about"
                            className="text-sm font-semibold leading-6 text-slate-900 hover:text-blue-600 transition-colors duration-200"
                        >
                            About Us
                        </Link>
                        <Link
                            to="/about/how"
                            className="text-sm font-semibold leading-6 text-slate-900 hover:text-blue-600 transition-colors duration-200"
                        >
                            How It Works
                        </Link>
                        <div className="flex items-center gap-x-4 pl-4 border-l border-gray-200">
                            <a
                                href="tel:+16172370589"
                                className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 text-slate-400 group-hover:text-blue-500"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={1.6}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.95.68l1.06 3.18a1 1 0 01-.27 1.06L8.12 9.88a16 16 0 006 6l1.96-1.9a1 1 0 011.06-.27l3.18 1.06a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.16 21 3 14.84 3 7V5z"
                                    />
                                </svg>

                                +1 (617) 237-0589
                            </a>
                            <Link
                                to="/auth/login"
                                className="text-sm font-semibold leading-6 text-slate-500 hover:text-blue-600 transition-colors duration-200"
                            >
                                Log in
                            </Link>
                            <Link
                                to="/auth/signup"
                                className="group relative rounded-full px-5 py-2 text-sm font-semibold"
                            >
                                <span className="absolute inset-0 rounded-full bg-blue-500 transition-all duration-200 ease-in-out group-hover:bg-blue-600"></span>
                                <span className="absolute inset-0 rounded-full ring-2 ring-blue-400/40 transition-all duration-200 ease-in-out group-hover:ring-blue-500/40"></span>
                                <span className="relative text-white">Check Eligibility</span>
                            </Link>
                        </div>
                    </div>
                    {/* Mobile Menu Button */}
                    <button
                        type="button"
                        className="md:hidden inline-flex items-center justify-center rounded-full bg-slate-50/50 text-slate-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                        onClick={() => setIsMobileMenuOpen(true)}
                    >
                        <span className="sr-only">Open main menu</span>
                        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                    </button>
                </nav>

                {/* Mobile menu overlay */}
                <div
                    className={`fixed inset-0 z-50 transition-opacity duration-300 ${isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                        }`}
                >
                    <div
                        className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm"
                        aria-hidden="true"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                    <div
                        className={`fixed inset-y-0 inset-x-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
                            }`}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                            <Link
                                to="/"
                                className="flex items-center space-x-2"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                <img
                                    src={logo}
                                    alt="Emissary Health"
                                    className="h-8 w-auto"
                                />
                            </Link>
                            <button
                                type="button"
                                className="rounded-full p-2 text-slate-500 hover:text-blue-600 hover:bg-slate-100 transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                <span className="sr-only">Close menu</span>
                                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="px-6 py-8 space-y-8 bg-white h-[calc(100vh-72px)] overflow-y-auto">
                            <div className="space-y-5">
                                <Link
                                    to="/about"
                                    className="flex items-center gap-x-2 text-base font-medium text-slate-800 hover:text-blue-600 transition-colors duration-200"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 ring-1 ring-slate-200">
                                        <UserGroupIcon className="h-5 w-5 text-slate-600" />
                                    </div>
                                    About Us
                                </Link>
                                <Link
                                    to="/about/how"
                                    className="flex items-center gap-x-2 text-base font-medium text-slate-800 hover:text-blue-600 transition-colors duration-200"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 ring-1 ring-slate-200">
                                        <SparklesIcon className="h-5 w-5 text-slate-600" />
                                    </div>
                                    How It Works
                                </Link>
                            </div>
                            <div className="space-y-5 pt-6 border-t border-slate-200">
                                <a
                                    href="tel:+16172370589"
                                    className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 text-slate-400 group-hover:text-blue-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={1.6}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.95.68l1.06 3.18a1 1 0 01-.27 1.06L8.12 9.88a16 16 0 006 6l1.96-1.9a1 1 0 011.06-.27l3.18 1.06a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.16 21 3 14.84 3 7V5z"
                                        />
                                    </svg>

                                    +1 (617) 237-0589
                                </a>
                                <Link
                                    to="/auth/login"
                                    className="flex items-center gap-x-2 text-base font-medium text-slate-800 hover:text-blue-600 transition-colors duration-200"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 ring-1 ring-slate-200">
                                        <UserIcon className="h-5 w-5 text-slate-600" />
                                    </div>
                                    Log in
                                </Link>
                                <Link
                                    to="/auth/signup"
                                    className="flex items-center justify-between w-full rounded-xl bg-blue-500 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-600 transition-colors duration-200 group"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    Check Eligibility
                                    <ArrowRightIcon className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
                                </Link>
                            </div>
                            <div className="pt-6 border-t border-slate-200">
                                <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                                    <p className="text-sm text-slate-600">
                                        Medicare-covered therapy services available
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Regular Header */}
            <header className="relative z-40">
                <nav
                    className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
                    aria-label="Global"
                >
                    <div className="flex lg:flex-1">
                        <Link to="/" className="flex items-center space-x-2 group">
                            <img
                                src={logo}
                                alt="Emissary Health"
                                className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
                            />
                        </Link>
                    </div>
                    <div className="hidden lg:flex lg:flex-1 lg:justify-center">
                        <div className="flex gap-x-12">
                            <Link
                                to="/about"
                                className="text-sm font-medium leading-6 text-slate-600 hover:text-blue-600 transition-colors duration-200"
                            >
                                About Us
                            </Link>
                            <Link
                                to="/about/how"
                                className="text-sm font-medium leading-6 text-slate-600 hover:text-blue-600 transition-colors duration-200"
                            >
                                How It Works
                            </Link>
                        </div>
                    </div>
                    <div className="flex lg:flex-1 lg:justify-end">
                        <div className="flex items-center gap-x-4">
                            <a
                                href="tel:+16172370589"
                                className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 text-slate-400 group-hover:text-blue-500"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={1.6}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.95.68l1.06 3.18a1 1 0 01-.27 1.06L8.12 9.88a16 16 0 006 6l1.96-1.9a1 1 0 011.06-.27l3.18 1.06a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.16 21 3 14.84 3 7V5z"
                                    />
                                </svg>

                                +1 (617) 237-0589
                            </a>
                            <Link
                                to="/auth/login"
                                className="text-sm font-medium leading-6 text-slate-500 hover:text-blue-600 transition-colors duration-200"
                            >
                                Log in
                            </Link>
                            <Link
                                to="/auth/signup"
                                className="group relative rounded-lg px-4 py-2 text-sm font-semibold"
                            >
                                <span className="absolute inset-0 rounded-lg bg-blue-500 transition-all duration-200 ease-in-out group-hover:bg-blue-600"></span>
                                <span className="absolute inset-0 rounded-lg ring-2 ring-blue-400/40 transition-all duration-200 ease-in-out group-hover:ring-blue-500/40"></span>
                                <span className="relative text-white">Check Eligibility</span>
                            </Link>
                        </div>
                    </div>
                </nav>
            </header>
        </div>
    )
}

export default PublicHeader